import React from "react";
import {
  Box,
  Container,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ImageList,
  ImageListItem,
  styled,
} from "@mui/material";
import { motion } from "framer-motion";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import { useTranslation } from "react-i18next";

import rn_img1 from "../../images/reinforcement/1.png";
import rn_img2 from "../../images/reinforcement/2.png";
import rn_img3 from "../../images/reinforcement/3.png";
import rn_img4 from "../../images/reinforcement/4.png";
import rn_img5 from "../../images/reinforcement/5.png";
import rn_img6 from "../../images/reinforcement/6.png";
import rn_img7 from "../../images/reinforcement/7.png";
import rn_img8 from "../../images/reinforcement/8.png";
import rn_img9 from "../../images/reinforcement/9.png";
import rn_img10 from "../../images/reinforcement/10.png";
import rn_img11 from "../../images/reinforcement/11.png";
import rn_img12 from "../../images/reinforcement/12.png";
import rn_img13 from "../../images/reinforcement/13.png";
import rn_img14 from "../../images/reinforcement/14.png";
import rn_img15 from "../../images/reinforcement/15.png";
import rn_img16 from "../../images/reinforcement/16.png";
import rn_img17 from "../../images/reinforcement/17.png";
import rn_img18 from "../../images/reinforcement/18.png";
import rn_img19 from "../../images/reinforcement/19.png";
import JccCompanyMobileView from "components/features/JccCompanyMobileView";

// Image Array for Dynamic Rendering
const images = [
  rn_img1,
  rn_img2,
  rn_img3,
  rn_img4,
  rn_img5,
  rn_img6,
  rn_img7,
  rn_img8,
  rn_img9,
  rn_img10,
  rn_img11,
  rn_img12,
  rn_img13,
  rn_img14,
  rn_img15,
  rn_img16,
  rn_img17,
  rn_img18,
  rn_img19,
];

const SectionHeader = styled(Box)(({ theme }) => ({
  background: "#f5f5f5",
  color: "black",
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  marginBottom: theme.spacing(2),
}));

const StyledImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  borderRadius: "8px",
});

const ImageCaption = styled(Typography)(({ theme }) => ({
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  backgroundColor: "rgba(255, 255, 255, 0.9)",
  padding: theme.spacing(1),
  fontSize: "0.7rem",
  color: "black",
  fontWeight: 670,
  textAlign: "center",
  borderBottomLeftRadius: "8px",
  borderBottomRightRadius: "8px",
}));

const StyledListItem = styled(ListItem)({
  paddingLeft: 0,
  paddingRight: 0,
});

const ReinforcementInspectionMobileView = () => {
  const { t } = useTranslation();
  const reinforcementDetails = t("reinforcementDetails", {
    returnObjects: true,
  });
  let imageIndex = 0;

  return (
    <Box sx={{ minHeight: "100vh", pb: 4 }}>
      <Container maxWidth="sm" sx={{ py: 3 }}>
        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <Typography
            color={"black"}
            fontSize={"1rem"}
            fontWeight={650}
            variant="h5"
          >
            {reinforcementDetails.title}
          </Typography>

          <Box sx={{ borderLeft: "4px solid #0c4d96", pl: 1, mt: 3, mb: 2 }}>
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                lineHeight: 1.6,
                fontSize: "0.8rem",
              }}
            >
              {reinforcementDetails.subtitle}
            </Typography>
          </Box>

          <List sx={{ mb: 4, p: 2, borderRadius: "12px", bgcolor: "#f5f5f5" }}>
            {reinforcementDetails.list.map((item, idx) => (
              <StyledListItem key={idx}>
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <FiberManualRecordIcon
                    sx={{ fontSize: "0.7rem", color: "#0c4d96" }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={item}
                  primaryTypographyProps={{
                    variant: "body2",
                    color: "text.primary",
                    fontSize: "0.7rem",
                  }}
                />
              </StyledListItem>
            ))}
          </List>
        </motion.div>

        {/* Sections */}
        {reinforcementDetails.sections.map((section, sectionIndex) => {
          const sectionImages = images.slice(
            imageIndex,
            imageIndex + section.images
          );
          const sectionFooters = section.footerText;
          imageIndex += section.images;

          return (
            <motion.div
              key={sectionIndex}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: sectionIndex * 0.1 }}
            >
              <SectionHeader
                sx={{ mb: 3, borderLeft: "4px solid #0c4d96", pl: 1 }}
              >
                <Typography
                  fontSize={"0.8rem"}
                  variant="subtitle1"
                  fontWeight={650}
                >
                  {section.header1}
                </Typography>
                {section.header2 && (
                  <Typography
                    fontSize={"0.7rem"}
                    variant="body2"
                    sx={{ mt: 0.5, opacity: 0.9 }}
                  >
                    {section.header2}
                  </Typography>
                )}
              </SectionHeader>

              <ImageList
                sx={{
                  mb: 4,
                  overflow: "hidden",
                }}
                cols={2}
                gap={16}
              >
                {sectionImages.map((img, imgIdx) => (
                  <ImageListItem
                    key={imgIdx}
                    component={motion.div}
                    whileHover={{ scale: 1.02 }}
                    sx={{
                      overflow: "hidden",
                      borderRadius: 2,
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                      aspectRatio: "1",
                      position: "relative",
                    }}
                  >
                    <StyledImage
                      src={img}
                      alt={`Section ${sectionIndex + 1} - ${imgIdx + 1}`}
                      loading="lazy"
                    />
                    <ImageCaption>{sectionFooters[imgIdx]}</ImageCaption>
                  </ImageListItem>
                ))}
              </ImageList>
            </motion.div>
          );
        })}
      </Container>

      <Box>
        <JccCompanyMobileView />
      </Box>
    </Box>
  );
};

export default ReinforcementInspectionMobileView;
